import { AfterViewInit, Component, ElementRef, OnDestroy, ChangeDetectionStrategy, inject, output, viewChild, input, signal } from '@angular/core';
import { ThemeService } from '@app/services/theme.service';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-iframe-modal-shell',
  templateUrl: './iframe-modal-shell.component.html',
  styleUrls: ['./iframe-modal-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IframeModalShellComponent implements AfterViewInit, OnDestroy {
  private hostRef = inject(ElementRef);
  private themeService = inject(ThemeService);

  readonly src = input<SafeResourceUrl | string | null>(null);
  /** When true, hide the loading dots once the iframe fires `load` (file/font browsers). */
  readonly dismissLoadingOnLoad = input(false);
  readonly initialTitle = input('...');
  // eslint-disable-next-line @angular-eslint/no-output-native -- renaming the output would change every binding to it
  readonly close = output<void>();
  readonly iframe = viewChild<ElementRef<HTMLIFrameElement>>('iframe');
  readonly contentArea = viewChild<ElementRef<HTMLDivElement>>('contentArea');

  readonly title = signal('...');
  readonly loading = signal(true);
  readonly iframeHeight = signal(520);
  contentPad = 0;
  private modalDialogEl?: HTMLElement;
  private contentEl?: HTMLElement;
  private themeSub?: Subscription;
  private readonly messageHandler = (event: MessageEvent) => {
    const iframeWindow = this.iframe()?.nativeElement?.contentWindow;
    if (!iframeWindow || event.source !== iframeWindow) {
      return;
    }

    const data = event.data;
    if (!data || typeof data !== 'object' || data.source !== 'snoball-iframe-modal') {
      return;
    }

    if (typeof data.title === 'string' && data.title.trim()) {
      this.title.set(data.title);
    }

    if (data.padding != null) {
      this.applyContentPadding(Number(data.padding) || 0);
    }

    if (typeof data.height === 'number' && data.height > 0) {
      this.iframeHeight.set(Math.min(data.height, Math.round(window.innerHeight * 0.82)));
    }

    if (typeof data.width === 'number' && data.width > 0) {
      this.applyModalWidth(Math.min(data.width, Math.round(window.innerWidth * 0.92)));
    }

    if (data.action === 'ready' || data.action === 'meta' || data.action === 'title') {
      this.loading.set(false);
      this.applyStickyHeader();
    }

    if (data.action === 'close') {
      this.close.emit();
    }
  };

  ngAfterViewInit(): void {
    const initialTitle = this.initialTitle();
    if (initialTitle && initialTitle !== '...') {
      this.title.set(initialTitle);
    }
    this.modalDialogEl = this.hostRef.nativeElement.closest('.modal-dialog');
    this.contentEl = this.contentArea()?.nativeElement;
    window.addEventListener('message', this.messageHandler, false);
    this.themeSub = this.themeService.isDarkTheme$.subscribe((isDark) => {
      const iframeWindow = this.iframe()?.nativeElement?.contentWindow;
      if (!iframeWindow) {
        return;
      }

      iframeWindow.postMessage({
        source: 'snoball-iframe-modal-host',
        action: 'setTheme',
        theme: isDark ? 'dark' : 'light',
        uiMode: isDark ? 'dark' : 'light',
      }, '*');
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageHandler, false);
    this.themeSub?.unsubscribe();
  }

  onIframeLoad(): void {
    if (this.dismissLoadingOnLoad()) {
      this.loading.set(false);
    }
    const isDark = document.body.classList.contains('dark-theme');
    this.iframe()?.nativeElement?.contentWindow?.postMessage({
      source: 'snoball-iframe-modal-host',
      action: 'setTheme',
      theme: isDark ? 'dark' : 'light',
      uiMode: isDark ? 'dark' : 'light',
    }, '*');
    setTimeout(() => this.applyStickyHeader(), 600);
  }

  private applyStickyHeader(retries = 3): void {
    try {
      const doc = this.iframe()?.nativeElement?.contentDocument;
      if (!doc || !doc.body) {
        return;
      }
      if (doc.getElementById('snoball-sticky-inject')) {
        return;
      }

      const table = doc.querySelector('table');
      if (!table) {
        if (retries > 0) {
          setTimeout(() => this.applyStickyHeader(retries - 1), 400);
        }
        return;
      }

      const parent = table.parentElement;
      if (!parent) {
        return;
      }

      const isDark = document.body.classList.contains('dark-theme');
      const bg = isDark ? '#1f2230' : '#ffffff';
      let cumulativeTop = 0;
      const siblings = Array.from(parent.children) as HTMLElement[];

      for (const el of siblings) {
        if (el === table) {
          break;
        }
        const cs = doc.defaultView.getComputedStyle(el);
        const marginTop = parseFloat(cs.marginTop) || 0;
        const marginBottom = parseFloat(cs.marginBottom) || 0;
        el.style.position = 'sticky';
        el.style.top = (cumulativeTop - marginTop) + 'px';
        el.style.zIndex = '10';
        el.style.backgroundColor = bg;
        el.style.paddingTop = (marginTop + (parseFloat(cs.paddingTop) || 0)) + 'px';
        el.style.paddingBottom = (marginBottom + (parseFloat(cs.paddingBottom) || 0)) + 'px';
        el.style.marginTop = '0';
        el.style.marginBottom = '0';
        cumulativeTop += el.offsetHeight;
      }

      const thead = table.querySelector('thead');
      if (thead) {
        const cells = thead.querySelectorAll('th, td') as NodeListOf<HTMLElement>;
        cells.forEach(cell => {
          cell.style.position = 'sticky';
          cell.style.top = cumulativeTop + 'px';
          cell.style.zIndex = '9';
          cell.style.backgroundColor = bg;
        });
      }

      const marker = doc.createElement('meta');
      marker.id = 'snoball-sticky-inject';
      doc.head.appendChild(marker);
    } catch (e) {
      // Cross-origin or other error — fail silently
    }
  }

  private applyContentPadding(padding: number): void {
    const pad = Math.max(0, padding);
    if (this.contentPad === pad) {
      return;
    }
    this.contentPad = pad;
    if (this.contentEl) {
      this.contentEl.style.padding = pad ? `${pad}px` : '0';
    }
  }

  private applyModalWidth(width: number): void {
    if (this.modalDialogEl) {
      const padDouble = this.contentPad * 2;
      const padded = width + padDouble;
      this.modalDialogEl.style.maxWidth = `${padded}px`;
      this.modalDialogEl.style.width = `${padded}px`;
    }
  }
}
